import { useEffect, useRef, useState } from 'react'
import { Alert, App, Button, Input, Tag, Typography } from 'antd'
import styled from 'styled-components'
import { apiFetch } from '../../api'

const { Paragraph, Text, Title } = Typography
const { TextArea } = Input

const MAX_LENGTH = 2000
const COUNTER_THRESHOLD = 1800
const POLL_INTERVAL = 5000

const Box = styled.div`
  height: 320px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 12px;
`
const MessageWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.$isAdmin ? 'flex-end' : 'flex-start')};
  margin-bottom: 12px;
`
const Bubble = styled.div`
  display: inline-block;
  background: ${p => (p.$isAdmin ? '#f6ffed' : '#fafafa')};
  border: 1px solid ${p => (p.$isAdmin ? '#b7eb8f' : '#f0f0f0')};
  border-radius: ${p => (p.$isAdmin ? '12px 12px 0 12px' : '12px 12px 12px 0')};
  padding: 6px 10px 8px 10px;
  max-width: 80%;
  text-align: left;
  box-shadow: ${p => (p.$isAdmin ? '0 2px 3px rgba(72,187,120,0.2)' : '0 2px 3px rgba(0,0,0,0.05)')};
`
const Author = styled.div`
  color: #888;
  font-size: 12px;
  margin-bottom: 4px;
  font-weight: 600;
`
const Meta = styled(Text)`
  && {
    font-size: 11px;
    color: #aaa;
    display: block;
    text-align: ${p => (p.$isAdmin ? 'right' : 'left')};
  }
`
const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`

// 400 bodies may carry the error code as a plain string or as a class-validator array – match defensively
const extractErrorCode = data => {
  if (Array.isArray(data?.message)) return data.message.map(String).find(m => m.includes('textTooLong') || m.includes('emptyText'))

  return typeof data?.message === 'string' ? data.message : null
}

/**
 * HOR-15/HOR-16: communication thread between customer and admins inside the process detail.
 * Polls the thread every 5s while `active` (= the detail modal is open); the GET also marks the
 * thread as read for the whole admin team, so the parent refetches the table on modal close to
 * clear the unread indicator.
 */
export default function ServiceChatPanel({ watchServiceId, deleted, active }) {
  const { message } = App.useApp()
  const [messages, setMessages] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)

  const boxRef = useRef(null)
  // double-submit guard that does not depend on a re-render (Enter can fire twice in one tick)
  const sendingRef = useRef(false)
  const notFoundRef = useRef(false)

  // HOR-93: Stale-Guard – die Vorgangs-ID, die dieses Panel GERADE zeigt. Eine Antwort, die
  // fuer eine andere ID gestartet wurde, darf den Thread nicht ueberschreiben.
  const idRef = useRef(watchServiceId)
  idRef.current = watchServiceId

  // HOR-93: Wird die Panel-Instanz fuer einen anderen Vorgang weiterverwendet, gehoert der
  // bisherige Zustand (Nachrichten, notFound-Sperre, Entwurf) zum alten Thread und muss weg.
  // Muss VOR dem Polling-Effekt stehen, damit notFoundRef vor dem ersten load() sauber ist.
  useEffect(() => {
    notFoundRef.current = false
    setMessages([])
    setLoaded(false)
    setLoadFailed(false)
    setNotFound(false)
    setText('')
  }, [watchServiceId])

  const load = async () => {
    const id = watchServiceId

    if (!id || notFoundRef.current) return

    const result = await apiFetch(`/admin/watchServices/${id}/messages`)

    // HOR-93: inzwischen zeigt das Panel einen anderen Vorgang – Antwort verwerfen
    if (idRef.current !== id) return

    if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

    if (result.ok) {
      setMessages(Array.isArray(result.data) ? result.data : [])
      setLoaded(true)
      setLoadFailed(false)

      return
    }

    if (result.status === 404) {
      // unknown id – retrying will not help, stop polling and disable the composer
      notFoundRef.current = true
      setNotFound(true)

      return
    }

    // transient load error – the polling loop retries automatically, only surface it before the first success
    setLoadFailed(true)
  }

  useEffect(() => {
    if (!active || !watchServiceId) return

    let cancelled = false
    let timer = null

    const loop = async () => {
      await load()

      if (cancelled || notFoundRef.current) return

      timer = setTimeout(loop, POLL_INTERVAL)
    }

    loop()

    return () => {
      cancelled = true

      if (timer) clearTimeout(timer)
    }
  }, [active, watchServiceId])

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTo({ top: boxRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages.length])

  const send = async () => {
    const value = text.trim()

    if (!value || sendingRef.current || deleted || notFound) return

    if (value.length > MAX_LENGTH) {
      message.error(i18n('services.chat.tooLong'))

      return
    }

    sendingRef.current = true
    setSending(true)

    const id = watchServiceId

    const result = await apiFetch(`/admin/watchServices/${id}/messages`, {
      method: 'POST',
      body: { text: value },
    })

    sendingRef.current = false
    setSending(false)

    // HOR-93: Antwort gehoert zu einem inzwischen ersetzten Vorgang – nichts anfassen
    if (idRef.current !== id) return

    if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

    if (result.ok) {
      setText('')
      await load()

      return
    }

    if (result.errorType === 'network') {
      // transient – keep the drafted text so the admin can simply retry
      message.error(i18n('errors.network'))

      return
    }

    if (result.status === 404) {
      // process unknown or deleted in the meantime – disable the composer, keep the history readable
      notFoundRef.current = true
      setNotFound(true)

      return
    }

    if (result.status === 400) {
      message.error(i18n(extractErrorCode(result.data) === 'textTooLong' ? 'services.chat.tooLong' : 'common.error'))

      return
    }

    message.error(i18n('common.error'))
  }

  const composerDisabled = deleted || notFound

  return (
    <div>
      <Title level={5}>{i18n('services.chat.title')}</Title>
      <Box ref={boxRef}>
        {!loaded && loadFailed && (
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
            {i18n('errors.loadFailed')}
          </Text>
        )}
        {loaded && messages.length === 0 && (
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 16 }}>
            {i18n('services.chat.empty')}
          </Text>
        )}
        {messages.map(m => {
          const isAdmin = m.authorType === 'administrator'

          return (
            <MessageWrap key={m.id} $isAdmin={isAdmin}>
              <Bubble $isAdmin={isAdmin}>
                {/* customer bubbles show the author name; a null author means the account was deleted (HOR-3) */}
                {!isAdmin && <Author>{m.author?.name || i18n('services.deletedUser')}</Author>}
                {isAdmin && m.author?.name && <Author>{m.author.name}</Author>}
                {m.context === 'kva' && <Tag color="purple">{i18n('services.chat.contextKva')}</Tag>}
                {m.context === 'offer' && <Tag color="purple">{i18n('services.chat.contextOffer')}</Tag>}
                <Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>{m.text || ''}</Paragraph>
                <Meta $isAdmin={isAdmin} type="secondary">
                  {moment(m.createdAt).format('L LT')}
                </Meta>
              </Bubble>
            </MessageWrap>
          )
        })}
      </Box>
      {deleted && <Alert type="warning" showIcon message={i18n('services.chat.deletedHint')} style={{ marginBottom: 8 }} />}
      {!deleted && notFound && <Alert type="warning" showIcon message={i18n('services.chat.notFound')} style={{ marginBottom: 8 }} />}
      <TextArea
        placeholder={i18n('services.chat.placeholder')}
        value={text}
        disabled={composerDisabled}
        onChange={e => setText(e.target.value)}
        rows={2}
        onPressEnter={e => {
          if (!e.shiftKey) {
            e.preventDefault()
            send()
          }
        }}
      />
      <ComposerFooter>
        {text.length >= COUNTER_THRESHOLD && (
          <Text type={text.trim().length > MAX_LENGTH ? 'danger' : 'secondary'}>{i18n('services.chat.charCount', { count: text.length })}</Text>
        )}
        <Button type="primary" onClick={send} loading={sending} disabled={composerDisabled || sending || !text.trim()}>
          {i18n('services.chat.send')}
        </Button>
      </ComposerFooter>
    </div>
  )
}
